import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { FormControl, FormField, FormItem } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';

/**
 * Los siete límites son idénticos al crear y al editar, así que viven aquí en vez de
 * duplicarse en los dos formularios.
 */
const LIMIT_FIELDS = [
  { name: 'maxUsers', label: 'Usuarios' },
  { name: 'maxRegions', label: 'Regiones' },
  { name: 'maxActiveTopics', label: 'Temas activos' },
  { name: 'maxActiveActors', label: 'Actores activos' },
  { name: 'maxAiAnalysisMonthly', label: 'Análisis de IA al mes' },
  { name: 'maxAlerts', label: 'Alertas' },
  { name: 'maxScheduledReports', label: 'Reportes programados' },
] as const;

/**
 * El marcador es texto y no un número. Una cifra atenuada —da igual que sea "20" o "0"— se lee
 * como un valor ya escrito, y aquí el campo arranca vacío a propósito: la instrucción tiene que
 * distinguirse de lo que el administrador teclea.
 */
const LIMIT_PLACEHOLDER = 'Ingresar límite';

interface PlanLimitsFieldsProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  disabled?: boolean;
  testIdPrefix: string;
}

export function PlanLimitsFields<T extends FieldValues>({
  form,
  disabled,
  testIdPrefix,
}: PlanLimitsFieldsProps<T>) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {LIMIT_FIELDS.map(({ name, label }) => (
        <FormField
          key={name}
          control={form.control}
          name={name as Path<T>}
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <Input
                  label={label}
                  placeholder={LIMIT_PLACEHOLDER}
                  type="number"
                  min={0}
                  {...field}
                  // Vacio se manda como undefined y no como 0: `Number('') || 0` convertia un
                  // campo en blanco en un limite de cero sin avisar, y ademas dejaba el 0
                  // pegado en el campo, imposible de borrar para escribir otra cifra.
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? undefined : e.target.valueAsNumber)
                  }
                  disabled={disabled}
                  error={form.formState.errors[name]?.message as string | undefined}
                  data-testid={`${testIdPrefix}-${name}-input`}
                />
              </FormControl>
            </FormItem>
          )}
        />
      ))}
    </div>
  );
}
